/**
 * InfoTooltip — TSE-3 P6.
 *
 * Small "ⓘ" icon next to a control whose meaning isn't obvious. Hovers
 * to a tooltip with 1-2 sentences of copy. Keyboard-focusable so the
 * accessibility-tree exposes the same hint to screen readers.
 *
 * Built on the existing Radix-wrapped tooltip primitive at
 * `src/components/ui/tooltip.tsx`. We don't reach for a custom popover
 * — the design system already covers this.
 *
 * Pattern:
 *   <label>
 *     Density <InfoTooltip text="Compact tightens section padding for denser reports." />
 *   </label>
 *
 * Wrap a single tree-root <TooltipProvider> high enough that ALL
 * usages share it (the Sleek Template Studio shell handles that).
 */
import React from 'react';
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '../ui/tooltip';

export function InfoTooltip({
  text,
  testId,
  stopPropagation = false,
}: {
  text: string;
  testId?: string;
  /**
   * When true, swallow pointer/click/keyboard events on the trigger so the
   * tooltip can live inside a clickable container (e.g. a navigable KPI card)
   * without the tap also firing the container's onClick. (U5b)
   */
  stopPropagation?: boolean;
}): React.ReactElement {
  const swallow = stopPropagation
    ? {
        onPointerDown: (e: React.PointerEvent) => { e.stopPropagation(); },
        onClick: (e: React.MouseEvent) => { e.stopPropagation(); e.preventDefault(); },
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
        },
      }
    : {};
  // Self-contained: wraps its own TooltipProvider so tests that mount
  // ThemeTab / PagesTab / etc. in isolation (without the studio shell's
  // outer provider) still render without "must be used within
  // TooltipProvider" errors. Radix's TooltipProvider is cheap to mount;
  // a higher-up provider would short-circuit nested ones via context
  // merge, so there's no double-listener cost in production.
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            // No role="img" — this is a tooltip trigger, not an image. The
            // aria-label names the control (the Info glyph is aria-hidden);
            // Radix wires the tooltip content as the description on open.
            aria-label={text}
            className="inline-flex items-center text-text-muted hover:text-text-primary cursor-help align-middle ml-1"
            data-testid={testId ?? 'info-tooltip'}
            {...swallow}
          >
            <Info className="w-3 h-3" aria-hidden="true" />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm text-xs whitespace-pre-line overflow-visible" data-testid={testId ? `${testId}-content` : 'info-tooltip-content'}>
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
